import { useRouter } from 'next/router';
import '@/shared/features/quiz-folder/consts/events.quiz-folder';
import { trackEvent } from '@zep/logger/tracking';
import { showShareModal } from '@/components';
import { LockIcon, PlayFillIcon } from '@/icons';
import { type Folder, type FolderPlayAction, reportFolderError, toTrackingFailReason } from '@/shared/features/quiz-folder';
import { withFolderPlayQuery } from '@/shared/features/quiz-folder/utils/folderPlayUrl';
import { usePlayUrl } from '@/shared/hooks';
import { usePremiumAccess } from '@/shared/plan';
import { useFolderCtaScenario } from './useFolderCtaScenario';
import { useFolderPlay } from './useFolderPlay';

/**
 * 전체 재생을 시작할 수 없는 상태. 잠긴 퀴즈가 하나라도 있으면 서버가 `PLAN_PAYMENT`·`UNAVAILABLE` 로 내려준다.
 * 버튼은 숨기지 않고 비활성으로 남긴다.
 *
 * 빈 폴더(`EMPTY`)는 데스크톱 패널이 버튼 자체를 그리지 않는다(`showPlayButton`). 모바일 하단 바는 주 버튼 자리를
 * 비우지 않고 비활성으로 남기므로 여기에도 둔다.
 */
const DISABLED_ACTIONS = new Set<FolderPlayAction>(['PLAN_PAYMENT', 'UNAVAILABLE', 'EMPTY']);

/** 재생 버튼 왼쪽 아이콘. 잠겨서 못 여는 상태만 자물쇠다. */
const PLAY_ICON: Record<FolderPlayAction, JSX.Element> = {
  ENTER: <PlayFillIcon size={'2xs'} />,
  EXPERIENCE: <PlayFillIcon size={'2xs'} />,
  EMPTY: <PlayFillIcon size={'2xs'} />,
  LOGIN_REQUIRED: <PlayFillIcon size={'2xs'} />,
  PLAN_PAYMENT: <LockIcon size={'2xs'} />,
  UNAVAILABLE: <LockIcon size={'2xs'} />
};
const PLACE = 'folder_detail_cta';

/**
 * 폴더 상세 CTA 의 판정과 핸들러. 데스크톱 패널과 모바일 하단 푸터가 같은 결론을 쓴다.
 *
 * ⚠️ 소유자 전용이다 — `FolderDetailPageView` 가 소유자에게만 CTA 를 그린다.
 * 남의 폴더는 폴더 단위 재생이 없고(체험은 3문제에서 잠겨 이어 풀 수 없다) 퀴즈별 저장만 있다.
 */
export const useFolderCta = (folder: Folder) => {
  const router = useRouter();
  const {
    openFeaturePaywall
  } = usePremiumAccess();
  const cta = useFolderCtaScenario(folder);
  const play = useFolderPlay(folder.id);

  // 입장 공유 링크는 첫 퀴즈의 플레이 URL 에 폴더를 실은 것이다 — 받은 사람이 폴더 순서대로 이어 푼다
  // (플레이 화면의 공유와 같은 계약, `useSharePlayUrl`). 입장 코드는 맵 단위라 첫 퀴즈 것이 그대로 보인다.
  const firstQuiz = folder.items[0]?.quizset;
  const firstPlayUrl = usePlayUrl(firstQuiz?.entryMapHashId ?? '');
  const shareEntry = () => {
    if (!firstQuiz) return;
    showShareModal({
      title: folder.name,
      shareUrl: withFolderPlayQuery(firstPlayUrl, folder.id),
      contentType: 'QUIZSET',
      contentId: firstQuiz.id,
      contentOwnerId: folder.ownerId,
      enterCode: firstQuiz.enterCode,
      spaceHashId: firstQuiz.spaceHashId,
      isOwner: true,
      // 폴더 링크를 나누는 자리다. 첫 퀴즈 하나를 학급에 연결하는 UI 는 맥락에 맞지 않는다.
      hideClassroomLinking: true,
      trackLocation: 'folder_detail'
    });
  };
  const startPlay = () => {
    trackEvent('quiz_folder_play_entry_click', {
      place: PLACE,
      folder_id: folder.id,
      play_action: cta.playAction,
      quiz_count: folder.quizsetCount,
      entry_quiz_set_id: folder.items[0]?.quizsetId
    });
    // 전역 처리기는 인증 실패만 다룬다. 그 밖의 실패는 화면에 알리지 않고 기록만 남긴다(unhandled rejection 방지).
    void play.mutateAsync().catch((error: unknown) => {
      trackEvent('quiz_folder_play_start_fail', {
        place: PLACE,
        folder_id: folder.id,
        reason: toTrackingFailReason(error)
      });
      reportFolderError(error, {
        action: 'play_start',
        folderId: folder.id
      });
    });
  };
  return {
    showPayPlan: cta.showPayPlan,
    notice: cta.notice,
    /** 빈 폴더는 재생 버튼을 그리지 않는다. 잠긴 내 퀴즈만 막고 있으면 `플랜 결제` 가 그 자리를 대신한다. */
    showPlayButton: cta.playAction !== 'EMPTY' && !cta.showPayPlan,
    playIcon: PLAY_ICON[cta.playAction],
    isPlayDisabled: DISABLED_ACTIONS.has(cta.playAction) || play.isPending,
    startPlay,
    /** 빈 폴더는 나눌 링크가 없다. 잠긴 퀴즈가 있어도 링크 자체는 나눌 수 있다 — 접근은 받은 쪽에서 판정된다. */
    showShareButton: !!firstQuiz,
    shareEntry,
    // 결제 페이지로 곧장 보내지 않고 기능 페이월을 연다 — 쓸 수 있는 이용권을 가진 사용자에게 결제를 권하지 않는다.
    openPaywall: () => void openFeaturePaywall({
      requiredPlan: 'PRO',
      from: 'detail_locked_folder'
    }),
    goEdit: () => void router.push(`/update/folder/${folder.id}`)
  };
};